/**
 * NutritionLibraryScreen - Browse all recipes with search and filters
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Col, Container, Row } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Recipe, RecipeFilterCombination, RecipeApi } from '../../../Model/Diet/Recipe/Recipe';
import { ExploreWeaviateSearchApi, ExploreWeaviateSearch } from '../../../Model/Explore/ExploreWeaviateSearch';
import {
  RecipeSearchParams,
  DishList,
  CuisinesList,
  DietsList,
  SeasonList,
} from '../../../Model/Diet/Recipe/RecipeSearchParams';
import { TileCard } from '../../../Components/ExploreCards/TileCard';
import { Theme } from '../../../Theme/Theme';
import { Translation } from '../../../Components/Translation';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  padding-bottom: 48px;
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const SearchSection = styled.div`
  padding: 24px 0;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  font-size: 1rem;
  border: 2px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 221, 163, 0.1);
  }

  &::placeholder {
    color: ${Theme.Colors.warmGrey};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: ${Theme.Colors.warmGrey};
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 0;
  align-items: center;
`;

const FilterDropdown = styled.div`
  position: relative;
`;

const FilterButton = styled.button<{ active?: boolean; hasSelection?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1.5px solid
    ${({ active, hasSelection }) => (active || hasSelection ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  background: ${({ hasSelection }) => (hasSelection ? 'rgba(0, 221, 163, 0.08)' : 'white')};
  color: ${({ hasSelection }) => (hasSelection ? Theme.Colors.primary : Theme.Colors.textColor)};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${Theme.Colors.primary};
    background: rgba(0, 221, 163, 0.05);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.15s ease;
    transform: ${({ active }) => (active ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

const DropdownMenu = styled.div<{ visible: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 100;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  transform: ${({ visible }) => (visible ? 'translateY(0)' : 'translateY(-8px)')};
  transition: all 0.15s ease;
`;

const DropdownItem = styled.button<{ selected?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  font-size: 0.875rem;
  text-align: left;
  border: none;
  background: ${({ selected }) => (selected ? 'rgba(0, 221, 163, 0.1)' : 'transparent')};
  color: ${({ disabled }) => (disabled ? Theme.Colors.warmGrey : Theme.Colors.textColor)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: background 0.1s ease;

  &:hover {
    background: ${({ disabled, selected }) =>
      disabled ? 'transparent' : selected ? 'rgba(0, 221, 163, 0.15)' : Theme.Colors.lightestGrey};
  }

  &:first-of-type {
    border-radius: 12px 12px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 12px 12px;
  }
`;

const Checkbox = styled.span<{ checked?: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${({ checked }) => (checked ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 4px;
  background: ${({ checked }) => (checked ? Theme.Colors.primary : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.1s ease;

  &::after {
    content: '${({ checked }) => (checked ? '✓' : '')}';
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const PrepTimeChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PrepTimeChip = styled.button<{ active?: boolean }>`
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1.5px solid ${({ active }) => (active ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  background: ${({ active }) => (active ? Theme.Colors.primary : 'white')};
  color: ${({ active }) => (active ? 'white' : Theme.Colors.textColor)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${Theme.Colors.primary};
  }
`;

const ActiveFiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  flex-wrap: wrap;
`;

const ActiveFilterTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  font-size: 0.85rem;
  color: ${Theme.Colors.textColor};
`;

const RemoveFilterButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 16px;
  cursor: pointer;
  color: ${Theme.Colors.warmGrey};
  line-height: 1;

  &:hover {
    color: ${Theme.Colors.textColor};
  }
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ResultsCount = styled.span`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  padding: 8px 0;
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${Theme.Colors.lighterGrey};
  margin: 0 4px;
`;

const RecipesGridWrapper = styled.div<{ isLoading?: boolean }>`
  position: relative;
  transition: opacity 0.2s ease;
  opacity: ${({ isLoading }) => (isLoading ? 0.6 : 1)};
  pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'auto')};
`;

const RecipesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const LoadMoreSentinel = styled.div`
  height: 1px;
  margin-top: 32px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  gap: 12px;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${Theme.Colors.lighterGrey};
  border-top-color: ${Theme.Colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.span`
  font-size: 0.875rem;
  color: ${Theme.Colors.charcoal};
`;

const NoResultsContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const NoResultsIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const NoResultsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const NoResultsText = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const SkeletonCard = styled.div`
  aspect-ratio: 1;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const PREP_TIME_OPTIONS = [
  { label: '< 15 min', maxReadyTime: 15 },
  { label: '< 30 min', maxReadyTime: 30 },
  { label: '< 45 min', maxReadyTime: 45 },
  { label: '< 60 min', maxReadyTime: 60 },
  { label: 'Any', maxReadyTime: undefined },
];

const ChevronDown = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export type NutritionLibraryScreenProps = {};

export const NutritionLibraryScreen: React.FC<NutritionLibraryScreenProps> = observer(() => {
  const { t } = useTrans();

  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<RecipeSearchParams>({});
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [validCombinations, setValidCombinations] = useState<RecipeFilterCombination>({
    diets: [],
    dishTypes: [],
    cuisines: [],
    seasons: [],
  });
  const [baseAvailableOptions, setBaseAvailableOptions] = useState<RecipeFilterCombination | null>(null);

  const hasMore = useRef(false);
  const searchTimeout = useRef<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const filterCombinationAbortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  const loadRecipes = useCallback((params: RecipeSearchParams, pageNum: number, append = false) => {
    if (loadingRef.current && append) return;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const currentRequestId = ++requestIdRef.current;

    loadingRef.current = true;
    setLoading(true);

    // Use Weaviate semantic search when there's a query (first page only)
    const hasQuery = params.query && params.query.trim().length >= 2;
    const useWeaviate = hasQuery && pageNum === 0 && !append;

    if (useWeaviate) {
      ExploreWeaviateSearch.search({
        concepts: [params.query!.trim()],
        filter: ['type:recipe'],
        autocut: 5,
      })
        .then((result) => {
          if (currentRequestId !== requestIdRef.current) return; // Stale request
          // Extract recipes from Weaviate result and filter by other criteria
          const weaviateRecipes = result.Content.map((r) => r.content as Recipe).filter(Boolean);
          setRecipes(weaviateRecipes);
          setTotalCount(weaviateRecipes.length);
          hasMore.current = false; // Weaviate returns all matches
        })
        .catch((err) => {
          if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
            console.error('Failed to search recipes:', err);
            // Fallback to regular search on error
            RecipeApi.search(Object.assign({}, params, { size: 24, page: pageNum })).then((res) => {
              setRecipes(res);
              hasMore.current = res.length >= 24;
            });
          }
        })
        .finally(() => {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false);
            loadingRef.current = false;
          }
        });
    } else {
      // Use regular search for filters-only or pagination
      Promise.all([RecipeApi.search(Object.assign({}, params, { size: 24, page: pageNum })), RecipeApi.count(params)])
        .then(([res, count]) => {
          if (currentRequestId !== requestIdRef.current) return; // Stale request
          if (append) {
            setRecipes((prev) => prev.concat(res));
          } else {
            setRecipes(res);
          }
          hasMore.current = res.length >= 24;
          setTotalCount(count);
        })
        .catch((err) => {
          if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
            console.error('Failed to load recipes:', err);
          }
        })
        .finally(() => {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false);
            loadingRef.current = false;
          }
        });
    }
  }, []);

  const loadFilterCombinations = useCallback((params: RecipeSearchParams, isInitialOrClear = false) => {
    if (filterCombinationAbortRef.current) {
      filterCombinationAbortRef.current.abort();
    }
    filterCombinationAbortRef.current = new AbortController();

    RecipeApi.filterCombination(params)
      .then((result) => {
        setValidCombinations(result);
        // Store base options on initial load or clear (when no filters are set)
        if (isInitialOrClear) {
          setBaseAvailableOptions(result);
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Failed to load filter combinations:', err);
        }
      });
  }, []);

  // Initial load - set base available options
  useEffect(() => {
    loadRecipes({}, 0);
    loadFilterCombinations({}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup: abort pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (filterCombinationAbortRef.current) {
        filterCombinationAbortRef.current.abort();
      }
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Load recipes when filter changes (but not on initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadRecipes(filter, 0);
    loadFilterCombinations(filter);
  }, [filter, loadRecipes, loadFilterCombinations]);

  useEffect(() => {
    if (page > 0) {
      loadRecipes(filter, page, true);
    }
  }, [page, filter, loadRecipes]);

  useEffect(() => {
    if (!hasMore.current) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current && hasMore.current) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [recipes.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdown && !(e.target as Element).closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setPage(0);
      setFilter((prev) => ({ ...prev, query: value || undefined }));
    }, 400);
  }, []);

  const toggleFilter = useCallback(<K extends keyof RecipeSearchParams>(key: K, value: any) => {
    setPage(0);
    setFilter((prev) => {
      const current = (prev[key] as any[]) || [];
      const newValues = current.includes(value) ? current.filter((v: any) => v !== value) : [...current, value];
      return { ...prev, [key]: newValues.length > 0 ? newValues : undefined };
    });
  }, []);

  const handlePrepTimeChange = useCallback((maxReadyTime: number | undefined) => {
    setPage(0);
    setFilter((prev) => ({ ...prev, maxReadyTime }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setPage(0);
    setSearchQuery('');
    setFilter({});
    // Reload base available options when clearing
    loadFilterCombinations({}, true);
  }, [loadFilterCombinations]);

  const removeFilter = useCallback(<K extends keyof RecipeSearchParams>(key: K, value?: any) => {
    setPage(0);
    setFilter((prev) => {
      if (value !== undefined && Array.isArray(prev[key])) {
        const current = (prev[key] as any[]) || [];
        const newValues = current.filter((v: any) => v !== value);
        return { ...prev, [key]: newValues.length > 0 ? newValues : undefined };
      }
      return { ...prev, [key]: undefined };
    });
  }, []);

  const getTranslatedLabel = useCallback(
    (type: string, value: string) => {
      return t(`recipe.${type}.${value}`, { defaultValue: value.replace(/_/g, ' ') });
    },
    [t],
  );

  const activeFilters = [
    ...(filter.dishTypes?.map((v) => ({ key: 'dishTypes', value: v, label: getTranslatedLabel('dishType', v) })) || []),
    ...(filter.cuisine?.map((v) => ({ key: 'cuisine', value: v, label: getTranslatedLabel('cuisine', v) })) || []),
    ...(filter.diet?.map((v) => ({ key: 'diet', value: v, label: getTranslatedLabel('diet', v) })) || []),
    ...(filter.seasons?.map((v) => ({ key: 'seasons', value: v, label: getTranslatedLabel('season', v) })) || []),
    ...(filter.maxReadyTime
      ? [{ key: 'maxReadyTime', value: filter.maxReadyTime, label: `< ${filter.maxReadyTime} min` }]
      : []),
  ];

  const renderSkeletonCards = () => Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={`skeleton-${i}`} />);

  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <Translation i18nKey="nutritionLibrary.title" defaultValue="Recipe Library" />
          </HeroTitle>
          <HeroSubtitle>
            <Translation
              i18nKey="nutritionLibrary.description"
              defaultValue="Browse our entire collection of healthy recipes. Use filters to find exactly what you're looking for."
            />
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      <Container>
        <SearchSection>
          <SearchInputWrapper>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder={t('nutritionLibrary.searchPlaceholder', { defaultValue: 'Search recipes...' })}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </SearchInputWrapper>
        </SearchSection>

        <FiltersBar>
          <PrepTimeChips>
            {PREP_TIME_OPTIONS.map((option) => (
              <PrepTimeChip
                key={option.label}
                active={filter.maxReadyTime === option.maxReadyTime}
                onClick={() => handlePrepTimeChange(option.maxReadyTime)}
              >
                {option.label}
              </PrepTimeChip>
            ))}
          </PrepTimeChips>

          <FilterDivider />

          <FilterDropdown data-dropdown>
            <FilterButton
              active={openDropdown === 'dishTypes'}
              hasSelection={(filter.dishTypes?.length ?? 0) > 0}
              onClick={() => setOpenDropdown(openDropdown === 'dishTypes' ? null : 'dishTypes')}
            >
              {t('nutritionLibrary.mealType', { defaultValue: 'Meal Type' })}
              {(filter.dishTypes?.length ?? 0) > 0 && ` (${filter.dishTypes?.length})`}
              <ChevronDown />
            </FilterButton>
            <DropdownMenu visible={openDropdown === 'dishTypes'}>
              {DishList.slice(0, 10)
                .filter((dish) => !baseAvailableOptions || baseAvailableOptions.dishTypes.includes(dish))
                .map((dish) => {
                  const isSelected = filter.dishTypes?.includes(dish);
                  const isAvailable =
                    validCombinations.dishTypes.length === 0 ||
                    validCombinations.dishTypes.includes(dish) ||
                    isSelected;
                  return (
                    <DropdownItem
                      key={dish}
                      selected={isSelected}
                      disabled={!isAvailable}
                      onClick={() => isAvailable && toggleFilter('dishTypes', dish)}
                    >
                      <Checkbox checked={isSelected} />
                      {getTranslatedLabel('dishType', dish)}
                    </DropdownItem>
                  );
                })}
            </DropdownMenu>
          </FilterDropdown>

          <FilterDropdown data-dropdown>
            <FilterButton
              active={openDropdown === 'diet'}
              hasSelection={(filter.diet?.length ?? 0) > 0}
              onClick={() => setOpenDropdown(openDropdown === 'diet' ? null : 'diet')}
            >
              {t('nutritionLibrary.diet', { defaultValue: 'Diet' })}
              {(filter.diet?.length ?? 0) > 0 && ` (${filter.diet?.length})`}
              <ChevronDown />
            </FilterButton>
            <DropdownMenu visible={openDropdown === 'diet'}>
              {DietsList.filter((diet) => !baseAvailableOptions || baseAvailableOptions.diets.includes(diet)).map(
                (diet) => {
                  const isSelected = filter.diet?.includes(diet);
                  const isAvailable =
                    validCombinations.diets.length === 0 || validCombinations.diets.includes(diet) || isSelected;
                  return (
                    <DropdownItem
                      key={diet}
                      selected={isSelected}
                      disabled={!isAvailable}
                      onClick={() => isAvailable && toggleFilter('diet', diet)}
                    >
                      <Checkbox checked={isSelected} />
                      {getTranslatedLabel('diet', diet)}
                    </DropdownItem>
                  );
                },
              )}
            </DropdownMenu>
          </FilterDropdown>

          <FilterDropdown data-dropdown>
            <FilterButton
              active={openDropdown === 'cuisine'}
              hasSelection={(filter.cuisine?.length ?? 0) > 0}
              onClick={() => setOpenDropdown(openDropdown === 'cuisine' ? null : 'cuisine')}
            >
              {t('nutritionLibrary.cuisine', { defaultValue: 'Cuisine' })}
              {(filter.cuisine?.length ?? 0) > 0 && ` (${filter.cuisine?.length})`}
              <ChevronDown />
            </FilterButton>
            <DropdownMenu visible={openDropdown === 'cuisine'}>
              {CuisinesList.filter(
                (cuisine) => !baseAvailableOptions || baseAvailableOptions.cuisines.includes(cuisine),
              ).map((cuisine) => {
                const isSelected = filter.cuisine?.includes(cuisine);
                const isAvailable =
                  validCombinations.cuisines.length === 0 || validCombinations.cuisines.includes(cuisine) || isSelected;
                return (
                  <DropdownItem
                    key={cuisine}
                    selected={isSelected}
                    disabled={!isAvailable}
                    onClick={() => isAvailable && toggleFilter('cuisine', cuisine)}
                  >
                    <Checkbox checked={isSelected} />
                    {getTranslatedLabel('cuisine', cuisine)}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </FilterDropdown>

          <FilterDropdown data-dropdown>
            <FilterButton
              active={openDropdown === 'seasons'}
              hasSelection={(filter.seasons?.length ?? 0) > 0}
              onClick={() => setOpenDropdown(openDropdown === 'seasons' ? null : 'seasons')}
            >
              {t('nutritionLibrary.season', { defaultValue: 'Season' })}
              {(filter.seasons?.length ?? 0) > 0 && ` (${filter.seasons?.length})`}
              <ChevronDown />
            </FilterButton>
            <DropdownMenu visible={openDropdown === 'seasons'}>
              {SeasonList.filter(
                (season) => !baseAvailableOptions || baseAvailableOptions.seasons.includes(season),
              ).map((season) => {
                const isSelected = filter.seasons?.includes(season);
                const isAvailable =
                  validCombinations.seasons.length === 0 || validCombinations.seasons.includes(season) || isSelected;
                return (
                  <DropdownItem
                    key={season}
                    selected={isSelected}
                    disabled={!isAvailable}
                    onClick={() => isAvailable && toggleFilter('seasons', season)}
                  >
                    <Checkbox checked={isSelected} />
                    {getTranslatedLabel('season', season)}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </FilterDropdown>
        </FiltersBar>

        {activeFilters.length > 0 && (
          <ActiveFiltersBar>
            {activeFilters.map((f, i) => (
              <ActiveFilterTag key={`${f.key}-${f.value}-${i}`}>
                {f.label}
                <RemoveFilterButton onClick={() => removeFilter(f.key as keyof RecipeSearchParams, f.value)}>
                  ×
                </RemoveFilterButton>
              </ActiveFilterTag>
            ))}
            <ClearAllButton onClick={clearAllFilters}>
              {t('nutritionLibrary.clearAll', { defaultValue: 'Clear all' })}
            </ClearAllButton>
          </ActiveFiltersBar>
        )}

        <ResultsCount>
          {totalCount > 0
            ? t('nutritionLibrary.resultsCount', { count: totalCount, defaultValue: `${totalCount} recipes found` })
            : t('nutritionLibrary.noResults', { defaultValue: 'No recipes found' })}
        </ResultsCount>

        {recipes.length === 0 && loading ? (
          <RecipesGrid>{renderSkeletonCards()}</RecipesGrid>
        ) : recipes.length === 0 && !loading ? (
          <NoResultsContainer>
            <NoResultsIcon>🍽️</NoResultsIcon>
            <NoResultsTitle>
              {t('nutritionLibrary.noResultsTitle', { defaultValue: 'No recipes found' })}
            </NoResultsTitle>
            <NoResultsText>
              {t('nutritionLibrary.noResultsText', {
                defaultValue: 'Try adjusting your filters or search query',
              })}
            </NoResultsText>
          </NoResultsContainer>
        ) : (
          <RecipesGridWrapper isLoading={loading}>
            <RecipesGrid>
              {recipes.map((recipe) => (
                <TileCard key={recipe.id} entry={recipe} linkPrefix="/nutrition/" />
              ))}
            </RecipesGrid>
            {hasMore.current && (
              <>
                <LoadMoreSentinel ref={sentinelRef} />
                {loading && recipes.length > 0 && (
                  <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>{t('globals.loading', { defaultValue: 'Loading...' })}</LoadingText>
                  </LoadingContainer>
                )}
              </>
            )}
          </RecipesGridWrapper>
        )}
      </Container>
    </ScreenContainer>
  );
});
